import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BellRingIcon } from "lucide-react";
import { AlertsView } from "@/components/alerts/alerts-view";
import type { AlertCounts } from "@/core/lib/alerts-api";
import { alertCountsQuery } from "@/lib/loaders";
import { PageHeaderSkeleton, skeletonKeys } from "@/ui/components/skeletons/page-skeletons";
import { Skeleton } from "@/ui/components/ui/skeleton";

const EMPTY_COUNTS: AlertCounts = {
  suggested: 0,
  upcoming: 0,
  due: 0,
  unread: 0,
};

/**
 * Alerts & reminders — the timeline of important employee dates and document
 * follow-ups. Suggestions are auto-derived on the backend (employee dates +
 * generated documents); the user confirms them here, and the daily job emails
 * confirmed reminders when they come due.
 */
export const Route = createFileRoute("/_workspace/alerts")({
  loader: ({ context }) => context.queryClient.ensureQueryData(alertCountsQuery),
  component: AlertsPage,
  pendingComponent: AlertsLoading,
});

function AlertsPage() {
  const { data: counts } = useQuery(alertCountsQuery);

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
            <BellRingIcon className="size-3.5" />
            Alerts
          </p>
          <h1 className="text-2xl font-bold tracking-tight">Reminders &amp; alerts</h1>
          <p className="text-sm text-muted-foreground">
            Stay ahead of contract ends, probation reviews, document follow-ups, and other key dates
            for your employees. Confirm suggestions and we'll remind you by email when they're due.
          </p>
        </div>
        <AlertsView initialCounts={counts ?? EMPTY_COUNTS} />
      </div>
    </main>
  );
}

function AlertsLoading() {
  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <PageHeaderSkeleton />
        <div className="flex flex-wrap gap-2">
          {skeletonKeys(4, "tab").map((key) => (
            <Skeleton key={key} className="h-9 w-28 rounded-md" />
          ))}
        </div>
        <div className="space-y-3">
          {skeletonKeys(5, "item").map((key) => (
            <Skeleton key={key} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </main>
  );
}
